/**
 * localStorage-backed state machine enforcing the hard caps on post-help invite
 * prompts (C3/C4). One instance per user id; all reads/writes are try/catch
 * guarded by hand rather than via defineStoredValue: the state is a hand-written
 * JSON record (timestamp list + permanent latch), and an in-memory mirror of it
 * could go stale against another tab's writes. It uses LOCALSTORAGE, not
 * sessionStorage - these caps are permanent by design (a prompt shown three
 * times in a lifetime, or once the user shares/dismisses, must stay stopped
 * across sessions).
 *
 * Fail-CLOSED on read: on any storage read error, `canPrompt()` returns false.
 * A prompt that never appears is strictly better than one that nags a user
 * whose storage we cannot trust - quieter beats louder for growth nudges.
 * Known gap, fail-OPEN on write: a write storage refuses is swallowed by `write()`,
 * so the next `canPrompt()` reads the empty state and the caps never accrue.
 * Nothing guards that path.
 *
 * Stored value at key `invite-prompts.v2.{userId}` (see STORAGE_PREFIX):
 *   { promptedAt: number[]; stoppedForever: boolean }
 * where promptedAt is the list of epoch-ms timestamps the user was prompted, and
 * stoppedForever latches true the moment they share OR dismiss.
 */
import { INVITE_PROMPTS } from '@config/invitePrompts';

interface CapState {
  promptedAt: number[];
  stoppedForever: boolean;
}

// v2 deliberately ORPHANS every pre-02ccf706 blob. Before that fix a shared
// dismiss sink could latch stoppedForever:true for users who never shared or
// dismissed, and the latch is permanent localStorage - poisoned dogfooder
// devices would stay silently dead forever. Bumping the prefix (NOT migrating -
// migration would carry the poison forward) lets those devices read fresh state.
// Worst case pre-GA: one extra prompt cycle, accepted in the spec. The explicit
// banner-dismiss latch is a SEPARATE util/key (inviteBannerDismissed.ts) and is
// intentionally kept.
const STORAGE_PREFIX = 'invite-prompts.v2.';
const keyFor = (userId: string) => `${STORAGE_PREFIX}${userId}`;

const read = (userId: string): CapState | null => {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return { promptedAt: [], stoppedForever: false };
    // Parsed shape is untrusted (older/corrupt payloads), so normalize both
    // fields defensively rather than assuming the current schema.
    const parsed = JSON.parse(raw) as Partial<CapState>;
    return {
      promptedAt: Array.isArray(parsed.promptedAt) ? parsed.promptedAt : [],
      stoppedForever: parsed.stoppedForever === true,
    };
  } catch {
    return null; // storage broken -> fail closed
  }
};

const write = (userId: string, state: CapState): void => {
  try {
    localStorage.setItem(keyFor(userId), JSON.stringify(state));
  } catch {
    /* best-effort: a lost write just means the cap may reappear next mount */
  }
};

export const invitePromptCaps = (userId: string) => ({
  canPrompt(): boolean {
    const s = read(userId);
    if (!s || s.stoppedForever) return false;
    if (s.promptedAt.length >= INVITE_PROMPTS.LIFETIME_MAX) return false;
    if (s.promptedAt.length === 0) return true; // never prompted yet
    const last = s.promptedAt[s.promptedAt.length - 1];
    return Date.now() - last >= INVITE_PROMPTS.COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  },
  recordPrompted(): void {
    const s = read(userId);
    if (!s) return; // broken storage: don't fabricate state
    write(userId, { ...s, promptedAt: [...s.promptedAt, Date.now()] });
  },
  recordShared(): void {
    const s = read(userId) ?? { promptedAt: [], stoppedForever: false };
    write(userId, { ...s, stoppedForever: true });
  },
  recordDismissed(): void {
    const s = read(userId) ?? { promptedAt: [], stoppedForever: false };
    write(userId, { ...s, stoppedForever: true });
  },
});
